import { useNavigate } from 'react-router-dom'
import { useCreateClient } from '@/features/client/services'

import { useClientContext } from '@/context/client'
import { ClientRegimeFormData, ClientRegimeFormDataCpf } from '@/types/client'
import { AxiosError } from 'axios'
import { ROUTE_BUILDERS } from '@/config/routes'
import { useCreateClientCPF } from '../services/useCreateClientCpf'
import { TypeContrato } from '@/features/agreement/utils/enum'
import { useCreateAgreement } from '@/features/agreement/services/useCreateAgreement'

const PF_REGIME = TypeContrato.PESSOA_FISICA
const PF_PRODUCT = TypeContrato.INSS_ACIMA_DO_TETO

export function useClientForm() {
  const {
    informacoesCnpj,
    clienteData,
    contadorData,
    produtosSelecionados,
    id_client,
    verificacaoConcluida,
    setMessage,
    setIsDialogOpen,
    setIsClienteCriado,
    setRegimeTributario,
    setProdutosSelecionados,
    setCustomComissao,
    setComissaoEscrita,
    setSenhaProprietario,
    setBancoData,
    setConcordo,
    setIsMinutaEnviada,
    setVerificacaoConcluida
  } = useClientContext()

  const navigate = useNavigate()
  const { mutate: createCliente, status: createStatusClient } = useCreateClient()
  const { mutate: createClienteCpf } = useCreateClientCPF()
  const { mutate: createAgreement } = useCreateAgreement(id_client)
  const effectiveClientId =
    String(informacoesCnpj?.cnpjcpf ?? informacoesCnpj?.cnpj ?? informacoesCnpj?.cpf ?? id_client ?? '').replace(/\D/g, '')

  const normalizePfFormData = (formData: ClientRegimeFormDataCpf): ClientRegimeFormDataCpf => ({
    ...formData,
    regimeTributario: PF_REGIME,
    produtosSelecionados: [PF_PRODUCT]
  })

  const handleCreateCliente = async (data: ClientRegimeFormData) => {
    if (!data || !clienteData) {
      setMessage({
        type: 'error',
        text: 'Preencha todos os campos obrigatórios antes de criar o cliente.'
      })
      setIsDialogOpen(true)
      return
    }

    const info_client = {
      nome: clienteData.name,
      cpf: clienteData.cpf,
      email: clienteData.email,
      whatsapp: clienteData.whatsapp,
      password: data.senha,
      agencia: data.agencia,
      conta: data.conta,
      banco: data.banco
    }

    const info_contador = {
      nome: contadorData?.name || '',
      email: contadorData?.email || '',
      whatsapp: contadorData?.whatsapp || ''
    }

    const Regime_produto = {
      regimeTributario: data.regimeTributario,
      produtosSelecionados: data.produtosSelecionados,
      comissao: data.comissao,
      comissaoEscrita: data.comissaoEscrita,
      concordo: data.concordo
    }

    createCliente(
      { cnpj_data: informacoesCnpj, info_client, info_contador, Regime_produto },
      {
        onSuccess: () => {
          setIsClienteCriado(true)
          setVerificacaoConcluida(true)
          setIsDialogOpen(true)

          if (!effectiveClientId) {
            setMessage({
              type: 'error',
              text: 'A requisição já está em andamento ou o ID do cliente não está disponível.'
            })
            setIsDialogOpen(true)
            return
          }

          const contratos: TypeContrato[] = [
            TypeContrato.PRINCIPAL,

            ...produtosSelecionados?.map(produto => produto as TypeContrato)
          ]

          createAgreement(
            { contracts: contratos, typeRegime: data.regimeTributario ?? '' },
            {
              onSuccess: () => {
                setIsMinutaEnviada(true)
                setMessage({ type: 'success', text: 'Cliente e minuta criados com sucesso!' })
              },
              onError: error => {
                console.error('Erro ao criar minuta:', error)
                setMessage({
                  type: 'error',
                  text: 'ouve um erro ao criar a minuta.'
                })
                setIsDialogOpen(true)
              }
            }
          )
        },
        onError: error => {
          let errorMessage = 'Houve um erro ao criar o cliente.'
          if (error instanceof AxiosError) {
            if (error.response && error.response.data && error.response.data.error) {
              errorMessage = error.response.data.error
            }
          }
          setMessage({
            type: 'error',
            text: errorMessage
          })
          setIsDialogOpen(true)
        }
      }
    )
  }

  const handleRegimeFormSubmitCPF = async (formData: ClientRegimeFormDataCpf) => {
    const normalizedData = normalizePfFormData(formData)

    setRegimeTributario(normalizedData.regimeTributario)
    setProdutosSelecionados(normalizedData.produtosSelecionados)
    setCustomComissao(normalizedData.comissao)
    setComissaoEscrita(normalizedData.comissaoEscrita)
    setSenhaProprietario(normalizedData.senha || '')
    setBancoData({
      banco: normalizedData.banco || '',
      agencia: normalizedData.agencia || '',
      conta: normalizedData.conta || ''
    })
    setConcordo(normalizedData.concordo)

    await handleCreateClientContratosCPF(normalizedData)
  }
  const handleRegimeFormSubmit = async (formData: ClientRegimeFormData) => {
    setRegimeTributario(formData.regimeTributario)
    setProdutosSelecionados(formData.produtosSelecionados)
    setCustomComissao(formData.comissao)
    setComissaoEscrita(formData.comissaoEscrita)
    setSenhaProprietario(formData.senha)
    setBancoData({
      banco: formData.banco || '',
      agencia: formData.agencia || '',
      conta: formData.conta || ''
    })
    setConcordo(formData.concordo)

    await handleCreateClienteComContratos(formData)
  }

  const handleCreateClienteComContratos = async (formData: ClientRegimeFormData) => {
    createCliente(
      {
        cnpj_data: informacoesCnpj,
        info_client: {
          nome: clienteData?.name,
          cpf: clienteData?.cpf,
          email: clienteData?.email,
          whatsapp: clienteData?.whatsapp,
          password: formData.senha,
          agencia: formData.agencia || '',
          conta: formData.conta || '',
          banco: formData.banco || '',
        },
        info_contador: {
          nome: contadorData?.name || '',
          email: contadorData?.email || '',
          whatsapp: contadorData?.whatsapp || ''
        },
        Regime_produto: {
          regimeTributario: formData.regimeTributario,
          produtosSelecionados: formData.produtosSelecionados,
          comissao: formData.comissao,
          comissaoEscrita: formData.comissaoEscrita,
          concordo: formData.concordo
        }
      },
      {
        onSuccess: () => {
          setIsClienteCriado(true)
          setVerificacaoConcluida(true)
          setIsDialogOpen(true)

          if (!effectiveClientId) {
            setMessage({
              type: 'error',
              text: 'A requisição já está em andamento ou o ID do cliente não está disponível.'
            })
            setIsDialogOpen(true)
            return
          }

          const contratos: TypeContrato[] = [
            TypeContrato.PRINCIPAL,
            ...(formData.produtosSelecionados?.map(produto => produto as TypeContrato) || [])
          ]

          createAgreement(
            { contracts: contratos, typeRegime: formData.regimeTributario ?? '' },
            {
              onSuccess: () => {
                setIsMinutaEnviada(true)
                setMessage({ type: 'success', text: 'Cliente e minuta criados com sucesso!' })
              },
              onError: error => {
                console.error('Erro ao criar minuta:', error)
                setMessage({
                  type: 'error',
                  text: 'Houve um erro ao criar a minuta.'
                })
                setIsDialogOpen(true)
              }
            }
          )
        },
        onError: error => {
          let errorMessage = 'Houve um erro ao criar o cliente.'
          if (error instanceof AxiosError) {
            if (error.response?.data?.error) {
              errorMessage = error.response.data.error
            }
          }
          setMessage({
            type: 'error',
            text: errorMessage
          })
          setIsDialogOpen(true)
        }
      }
    )
  }

  const handleCreateClientContratosCPF = async (formData: ClientRegimeFormDataCpf) => {
    createClienteCpf(
      {
        cnpj_data: informacoesCnpj,
        info_client: {
          nome: clienteData?.name,
          cpf: clienteData?.cpf,
          email: clienteData?.email,
          whatsapp: clienteData?.whatsapp,
          password: formData.senha,
          agencia: formData.agencia,
          conta: formData.conta,
          banco: formData.banco
        },
        info_contador: {
          nome: contadorData?.name || '',
          email: contadorData?.email || '',
          whatsapp: contadorData?.whatsapp || ''
        },
        Regime_produto: {
          regimeTributario: formData.regimeTributario,
          produtosSelecionados: formData.produtosSelecionados,
          comissao: formData.comissao,
          comissaoEscrita: formData.comissaoEscrita,
          concordo: formData.concordo
        }
      },
      {
        onSuccess: () => {
          setIsClienteCriado(true)
          setVerificacaoConcluida(true)
          setIsDialogOpen(true)

          if (!effectiveClientId) {
            setMessage({
              type: 'error',
              text: 'A requisição já está em andamento ou o ID do cliente não está disponível.'
            })
            setIsDialogOpen(true)
            return
          }

          const contratos: TypeContrato[] = [
            TypeContrato.PRINCIPAL,

            ...(formData.produtosSelecionados?.map(produto => produto as TypeContrato) || [])
          ]

          createAgreement(
            { contracts: contratos, typeRegime: formData.regimeTributario ?? '' },
            {
              onSuccess: () => {
                setIsMinutaEnviada(true)
                setMessage({ type: 'success', text: 'Cliente e minuta criados com sucesso!' })
              },
              onError: error => {
                console.error('Erro ao criar minuta:', error)
                setMessage({
                  type: 'error',
                  text: 'Houve um erro ao criar a minuta.'
                })
                setIsDialogOpen(true)
              }
            }
          )
        },
        onError: error => {
          let errorMessage = 'Houve um erro ao criar o cliente.'
          if (error instanceof AxiosError) {
            if (error.response?.data?.error) {
              errorMessage = error.response.data.error
            }
          }
          setMessage({
            type: 'error',
            text: errorMessage
          })
          setIsDialogOpen(true)
        }
      }
    )
  }

  const handleAssinarContrato = async () => {
    if (!effectiveClientId) {
      setMessage({
        type: 'error',
        text: 'ID do cliente não está definido. Verifique os dados.'
      })
      setIsDialogOpen(true)
      return
    }

    if (verificacaoConcluida) {
      try {
        navigate(ROUTE_BUILDERS.minute(effectiveClientId))
      } catch (error) {
        console.error('Erro na navegação para assinatura:', error)

        setMessage({
          type: 'error',
          text: 'Houve um problema ao navegar para a assinatura.'
        })
        setIsDialogOpen(true)
      }
    } else {
      setMessage({
        type: 'error',
        text: 'Verificação Incompleta! Conclua todas as verificações antes de prosseguir.'
      })
      setIsDialogOpen(true)
    }
  }

  return {
    createStatusClient,
    handleCreateCliente,
    handleRegimeFormSubmit,
    handleRegimeFormSubmitCPF,
    handleAssinarContrato
  }
}
